import { useCallback, useEffect, useState } from 'react';

// The optimistic-overlay mechanics shared by the two per-place Profile lists
// the swap picker writes: the ✕'s `locationUnavailable` and the ★'s
// `equipmentDefault`.
//
// ⭐ Extracted on the reviewer's call (EQ-1-PIN-DEFAULT, 2026-08-20). The
// second hook had been written deliberately in the first one's shape, with a
// comment saying so — but §CONSOLIDAMENTO's bar is "primitive absent + 2+ real
// callsites → build the primitive INSIDE the change", and the shape was
// identical down to the empty-array rule and the catch. Two copies of "write
// optimistically, roll back on failure" are exactly the pair that stops
// agreeing the day one of them grows a retry.
//
// ⛔ Only the MECHANICS live here. The domain rules stay in their own shared
// modules (locationUnavailable.ts, equipmentDefault.ts) because they genuinely
// differ: one records a set of things that are missing, the other a single
// election. Folding those together is what would let a demotion sink a row.
export function useProfileListOverlay<K extends string, T>({
  visible,
  place,
  source,
  field,
  patchProfile,
  notify,
  errorTitle,
  errorBody,
}: {
  /** The host surface's visibility — closing it drops the overlay. */
  visible: boolean;
  /** Re-keying place also drops it: an answer about one room is not about
   *  another. */
  place: string | null;
  /** The list as the host last read it off the Profile. */
  source: T[] | undefined;
  /** The Profile attribute this overlay writes. */
  field: K;
  patchProfile: (
    patch: Partial<Record<K, T[] | undefined>>,
  ) => Promise<void>;
  notify: (title: string, body: string) => void;
  errorTitle: () => string;
  errorBody: () => string;
}): {
  /** What the screen should show right now — the pending answer, else the
   *  server's. */
  list: T[] | undefined;
  write: (next: T[]) => void;
} {
  // ⭐ Optimistic and NOT cleared on success: both callers change something the
  // athlete is looking at (a row sinks, a tray stops asking), so waiting for a
  // profile refetch would read as the tap having been ignored.
  const [pending, setPending] = useState<T[] | null>(null);
  useEffect(() => {
    if (!visible) setPending(null);
  }, [visible, place]);

  const write = useCallback(
    (next: T[]) => {
      setPending(next);
      void patchProfile({
        // An empty list is sent as ABSENT, not as `[]`: the API's
        // `setIfPresent` removes an optional attribute missing from the body,
        // so clearing the last entry deletes the field instead of leaving a
        // stale array behind. Both hosts agree — mobile's door turns
        // `undefined` into an explicit clear, web's full-doc PUT drops the key.
        [field]: next.length > 0 ? next : undefined,
      } as Partial<Record<K, T[] | undefined>>).catch(() => {
        // Back to what the server is known to hold. A write that failed must
        // not leave the athlete looking at an answer nobody stored.
        setPending(null);
        notify(errorTitle(), errorBody());
      });
    },
    [field, patchProfile, notify, errorTitle, errorBody],
  );

  return { list: pending ?? source, write };
}
